<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-back-button text="" default-href="/showcase"></ion-back-button>
        </ion-buttons>
        <ion-title>Оплата</ion-title>
        <ion-buttons slot="end">
          <HeaderMessengerButton />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true">
      <div class="ion-padding payment-success-block">
        <template v-if="paymentState === 'checking'">
          <ion-spinner name="crescent" class="payment-status-spinner"></ion-spinner>
          <h1 class="payment-success-title">Проверяем оплату</h1>
          <p class="payment-success-text">Подождите, уточняем статус платежа…</p>
        </template>

        <template v-else-if="paymentState === 'paid'">
          <div class="payment-success-icon-wrap">
            <ion-icon :icon="checkmarkCircleOutline" class="payment-success-icon"></ion-icon>
          </div>
          <h1 class="payment-success-title">Оплата прошла</h1>
          <p class="payment-success-text">
            <template v-if="isSubscriptionPurchase">
              Спасибо за оплату. Подписка активирована<span v-if="accessRefreshed">, доступ обновлён</span>.
              Если статус не обновился, откройте «Мои подписки».
            </template>
            <template v-else-if="isReleasePurchase">
              Спасибо за оплату. Доступ к релизу подтверждён. Если контент не открылся, вернитесь в раздел «Релизы от Лиги».
            </template>
            <template v-else>
              Спасибо за оплату. Регистрация на событие подтверждена<span v-if="accessRefreshed">, доступ обновлён</span>.
            </template>
          </p>
          <ion-button v-if="isSubscriptionPurchase" expand="block" class="event-register-btn" @click="goToMySubscriptions">
            Мои подписки
          </ion-button>
          <ion-button v-else-if="!isReleasePurchase" expand="block" class="event-register-btn" @click="goToEvents">
            К событиям
          </ion-button>
          <ion-button v-if="isReleasePurchase" expand="block" class="event-register-btn" @click="goToPrograms">
            К релизам
          </ion-button>
          <ion-button
            v-if="isSubscriptionPurchase && subscriptionId"
            expand="block"
            fill="outline"
            class="event-detail-btn"
            @click="goToSubscription"
          >
            Вернуться к подписке
          </ion-button>
          <ion-button
            v-if="!isReleasePurchase && !isSubscriptionPurchase && eventId"
            expand="block"
            fill="outline"
            class="event-detail-btn"
            @click="goToEvent"
          >
            Вернуться к событию
          </ion-button>
        </template>

        <template v-else>
          <div class="payment-success-icon-wrap">
            <ion-icon :icon="closeCircleOutline" class="payment-pending-icon"></ion-icon>
          </div>
          <h1 class="payment-success-title">Оплата не завершена</h1>
          <p class="payment-success-text">
            Вы вернулись со страницы оплаты без подтверждения платежа. Если деньги всё же списались, подождите минуту и нажмите «Проверить снова».
          </p>
          <ion-button expand="block" class="event-register-btn" :disabled="checkingAgain" @click="checkPaymentStatus">
            {{ checkingAgain ? 'Проверяем…' : 'Проверить снова' }}
          </ion-button>
          <ion-button expand="block" fill="outline" class="event-detail-btn" @click="goBackToSource">
            {{ backButtonLabel }}
          </ion-button>
        </template>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar
} from '@ionic/vue';
import { checkmarkCircleOutline, closeCircleOutline } from 'ionicons/icons';
import { computed, defineComponent, inject, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AccountService from '@/services/account.service';
import eventsService from '@/services/events.service';
import releasesService from '@/services/releases.service';
import subscriptionsService from '@/services/subscriptions.service';

type PaymentState = 'checking' | 'paid' | 'unpaid';

import HeaderMessengerButton from '@/components/HeaderMessengerButton.vue';

export default defineComponent({
  name: 'EventPaymentSuccess',
  components: {
    HeaderMessengerButton,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonSpinner,
    IonTitle,
    IonToolbar
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const accountService = inject<AccountService>('accountService');
    const paymentState = ref<PaymentState>('checking');
    const checkingAgain = ref(false);
    const accessRefreshed = ref(false);

    const eventId = computed(() => {
      const id = route.query.eventId ?? route.params.eventId;
      return id ? String(id) : null;
    });
    const subscriptionId = computed(() => {
      const id = route.query.subscriptionId;
      return id ? String(id) : null;
    });
    const purchaseId = computed(() => {
      const id = route.query.purchaseId;
      return id ? String(id) : null;
    });
    const isSubscriptionPurchase = computed(() => {
      return route.query.subscriptionPaid === '1' || subscriptionId.value != null;
    });
    const isReleasePurchase = computed(() => {
      const pid = purchaseId.value ? Number(purchaseId.value) : 0;
      if (!pid || isSubscriptionPurchase.value) return false;
      return route.query.releasePaid === '1' || route.query.releaseId != null;
    });
    const backButtonLabel = computed(() => {
      if (isSubscriptionPurchase.value) return 'К подписке';
      if (isReleasePurchase.value) return 'К релизам';
      return 'К событию';
    });

    const goToEvents = () => router.push('/events');
    const goToPrograms = () => router.push('/programs');
    const goToMySubscriptions = () => router.push('/my-subscriptions');
    const goToEvent = () => {
      if (eventId.value) router.push(`/events/${eventId.value}`);
      else router.push('/events');
    };
    const goToSubscription = () => {
      if (subscriptionId.value) router.push(`/subscriptions/${subscriptionId.value}`);
      else router.push('/my-subscriptions');
    };
    const goBackToSource = () => {
      if (isSubscriptionPurchase.value) {
        goToSubscription();
        return;
      }
      if (isReleasePurchase.value) {
        goToPrograms();
        return;
      }
      goToEvent();
    };

    const checkPaymentStatus = async (isRetry = false) => {
      if (isRetry) checkingAgain.value = true;
      else paymentState.value = 'checking';

      let paid = false;
      try {
        if (isSubscriptionPurchase.value && subscriptionId.value) {
          paid = await subscriptionsService.syncMyPayment(Number(subscriptionId.value));
        } else if (isReleasePurchase.value && purchaseId.value) {
          paid = await releasesService.syncMyPurchase(Number(purchaseId.value));
        } else if (eventId.value) {
          paid = await eventsService.syncMyPayment(Number(eventId.value));
        }
      } finally {
        paymentState.value = paid ? 'paid' : 'unpaid';
        checkingAgain.value = false;
      }
      if (paid && !isReleasePurchase.value && accountService) {
        accessRefreshed.value = await accountService.refreshAuthoritiesAfterPurchase();
      }
    };

    onMounted(() => {
      void checkPaymentStatus();
    });

    return {
      checkmarkCircleOutline,
      closeCircleOutline,
      eventId,
      subscriptionId,
      isReleasePurchase,
      isSubscriptionPurchase,
      paymentState,
      checkingAgain,
      accessRefreshed,
      backButtonLabel,
      checkPaymentStatus,
      goToEvents,
      goToPrograms,
      goToMySubscriptions,
      goToEvent,
      goToSubscription,
      goBackToSource
    };
  }
});
</script>

<style scoped>
.payment-success-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding-top: 2rem;
}
.payment-status-spinner {
  width: 48px;
  height: 48px;
  margin-bottom: 1.5rem;
  color: var(--ion-color-orange, #ff6600);
}
.payment-success-icon-wrap {
  margin-bottom: 1.5rem;
}
.payment-success-icon {
  font-size: 80px;
  color: var(--ion-color-success);
}
.payment-pending-icon {
  font-size: 80px;
  color: var(--ion-color-warning, #ffc409);
}
.payment-success-title {
  font-size: 1.5rem;
  color: var(--liga-chrome-text);
  margin-bottom: 0.75rem;
}
.payment-success-text {
  color: var(--liga-chrome-text);
  margin-bottom: 2rem;
  max-width: 320px;
}
</style>
